/**
 * Pre-survey document portal service.
 *
 * All operations require authentication. Access is scoped by facility
 * assignment (user_scope_roles). Admins/editors can create document
 * requests; assigned reviewer roles can update statuses; all roles can
 * upload and view documents.
 */

import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

// ── Types ───────────────────────────────────────────────────────────────────

export type PrestudyDocType =
  | 'policy' | 'log' | 'certificate' | 'license'
  | 'insurance' | 'roster' | 'emergency_plan' | 'credential' | 'other'

export type PrestudyDocStatus = 'pending' | 'uploaded' | 'reviewed' | 'flagged'

export interface PrestudyRequest {
  link_id: string
  visit_id: string
  requested_docs: string[]
  created_by: string
  created_at: string
  survey_prestudy_documents?: PrestudyDocument[]
}

export interface PrestudyDocument {
  document_id: string
  link_id: string | null
  visit_id: string
  uploaded_by_name: string | null
  uploaded_by_email: string | null
  doc_type: PrestudyDocType
  label: string
  file_path: string | null
  file_size_bytes: number | null
  mime_type: string | null
  status: PrestudyDocStatus
  reviewer_notes: string | null
  created_at: string
  reviewed_at: string | null
}

interface PrestudyDocumentDownloadResponse {
  download_url: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function asPrestudyDocumentDownloadResponse(value: unknown): PrestudyDocumentDownloadResponse | null {
  if (!isRecord(value)) return null
  if (typeof value.download_url !== 'string' || !value.download_url.trim()) return null
  return { download_url: value.download_url }
}

// ── Suggested document categories for the request checklist ─────────────

export const PRESTUDY_DOC_CATEGORIES: { type: PrestudyDocType; label: string; description: string }[] = [
  { type: 'policy', label: 'Policies & Procedures', description: 'Infection control, medication management, emergency management, patient rights policies' },
  { type: 'credential', label: 'Credentialing Files', description: 'Physician privileges, nursing licenses, ACLS/BLS certifications' },
  { type: 'certificate', label: 'Certificates & Accreditations', description: 'State license, CMS certification, accreditation certificates' },
  { type: 'license', label: 'Equipment Licenses', description: 'DEA registration, radiology licenses, pharmacy permits' },
  { type: 'insurance', label: 'Insurance Documentation', description: 'Malpractice insurance, liability coverage' },
  { type: 'log', label: 'Logs & Records', description: 'Sterilizer logs, temperature logs, crash cart checks, emergency drill records' },
  { type: 'roster', label: 'Staff Roster', description: 'Current staff list with roles and certifications' },
  { type: 'emergency_plan', label: 'Emergency Management Plan', description: 'All-hazards EMP, evacuation plans, drill documentation' },
  { type: 'other', label: 'Other Documents', description: 'QAPI reports, governing body minutes, transfer agreements, any other relevant documents' },
]

// ── Document Requests (admin/editor) ─────────────────────────────────────

export async function createDocumentRequest(
  visitId: string,
  input: { requested_docs: string[]; notes?: string },
): Promise<PrestudyRequest> {
  const { data, error } = await invokeFunction<unknown>('prestudy-portal', {
    method: 'POST',
    path: `/visits/${visitId}/prestudy/requests`,
    body: input,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to create document request')
  return data as PrestudyRequest
}

export async function getDocumentRequests(visitId: string): Promise<PrestudyRequest[]> {
  const { data, error } = await invokeFunction<{ requests: unknown[] }>('prestudy-portal', {
    method: 'GET',
    path: `/visits/${visitId}/prestudy/requests`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load document requests')
  return (data?.requests ?? []) as PrestudyRequest[]
}

// ── Documents (all authenticated roles) ──────────────────────────────────

export async function getPrestudyDocuments(visitId: string): Promise<PrestudyDocument[]> {
  const { data, error } = await invokeFunction<{ documents: unknown[] }>('prestudy-portal', {
    method: 'GET',
    path: `/visits/${visitId}/prestudy/documents`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load prestudy documents')
  return (data?.documents ?? []) as PrestudyDocument[]
}

export async function uploadPrestudyDocument(
  visitId: string,
  input: {
    doc_type: PrestudyDocType
    label: string
    request_id?: string
    file: File
  },
): Promise<PrestudyDocument> {
  const formData = new FormData()
  formData.append('doc_type', input.doc_type)
  formData.append('label', input.label)
  if (input.request_id) formData.append('request_id', input.request_id)
  formData.append('file', input.file)

  const { data, error } = await invokeFunction<unknown>('prestudy-portal', {
    method: 'POST',
    path: `/visits/${visitId}/prestudy/documents/upload`,
    body: formData,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to upload document')
  return data as PrestudyDocument
}

export async function getPrestudyDocumentDownloadUrl(visitId: string, documentId: string): Promise<string> {
  const { data, error } = await invokeFunction<unknown>('prestudy-portal', {
    method: 'GET',
    path: `/visits/${visitId}/prestudy/documents/${documentId}:download`,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to open prestudy document')
  const signedData = asPrestudyDocumentDownloadResponse(data)
  if (!signedData) {
    throw new Error('Malformed prestudy document download response from server')
  }
  return signedData.download_url
}

export async function updateDocumentStatus(
  visitId: string,
  documentId: string,
  input: { status: PrestudyDocStatus; reviewer_notes?: string },
): Promise<PrestudyDocument> {
  const { data, error } = await invokeFunction<unknown>('prestudy-portal', {
    method: 'PATCH',
    path: `/visits/${visitId}/prestudy/documents/${documentId}`,
    body: input,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to update document status')
  return data as PrestudyDocument
}
